'use client';

import { createRoot, type Root } from 'react-dom/client';

import { Footer } from '@/features/footer';
import type { NewsletterFormValues } from '@/features/forms/components/newsletter';
import { submitNewsletter } from '@/features/forms/components/newsletter/submitNewsletter';
import { createWidgetMount } from '@/shared/utils/widget-mount';

import { OBSERVED_FOOTER_WIDGET_ATTRIBUTES, readFooterWidgetProps } from './footer-element-props';
import type { FooterEventName } from './types';

function dispatchFooterEvent(element: HTMLElement, name: FooterEventName, detail?: unknown): void {
  element.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail,
    }),
  );
}

export class FooterWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_FOOTER_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this, {
        rootAttribute: 'data-adipa-footer-widget-root',
      }) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const props = readFooterWidgetProps(this);

    dispatchFooterEvent(this, 'adipa-footer-ready', {
      homeUrl: props.homeUrl,
      newsletterSubmitUrl: props.newsletterSubmitUrl,
    });

    const newsletterCallbacks = {
      onSubmit: props.newsletterSubmitUrl
        ? async (values: NewsletterFormValues) => {
            dispatchFooterEvent(this, 'adipa-footer-newsletter-submit', values);
            await submitNewsletter({
              pageUrl: props.pageUrl,
              recaptchaAction: props.newsletterRecaptchaAction,
              recaptchaSiteKey: props.newsletterRecaptchaSiteKey,
              source: props.newsletterSource,
              url: props.newsletterSubmitUrl!,
              values,
            });
          }
        : (values: NewsletterFormValues) => {
            dispatchFooterEvent(this, 'adipa-footer-newsletter-submit', values);
          },
      onSuccess: (values: NewsletterFormValues) => {
        dispatchFooterEvent(this, 'adipa-footer-newsletter-success', values);
      },
      onError: (error: unknown) => {
        dispatchFooterEvent(this, 'adipa-footer-newsletter-error', {
          error: error instanceof Error ? error.message : String(error),
        });
      },
    };

    this.root.render(
      <Footer
        content={props.content}
        logoSrc={props.logoSrc}
        homeUrl={props.homeUrl}
        giftcardUrl={props.giftcardUrl}
        hideNewsletter={props.hideNewsletter}
        hideGiftcard={props.hideGiftcard}
        newsletter={newsletterCallbacks}
      />,
    );
  }
}
